/**
 * The Totum components from the styling guide, as React. Copied from the
 * guide's class lists rather than reinvented, so this app looks like the rest.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { formatChange } from '../lib/format';

export function Card({ title, actions, children, className = '' }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`bg-white rounded-xl p-6 shadow-sm border border-gray-200 ${className}`}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          {title && <h2 className="text-lg font-semibold text-gray-900">{title}</h2>}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

type ButtonKind = 'primary' | 'secondary' | 'quiet' | 'danger';

const BUTTON: Record<ButtonKind, string> = {
  primary: 'bg-totum-blue text-white hover:bg-totum-blue-hover',
  secondary: 'bg-white text-totum-ink border border-totum-blue hover:bg-totum-blue hover:text-white',
  quiet: 'bg-transparent text-gray-700 hover:bg-gray-100',
  danger: 'bg-white text-red-700 border border-red-300 hover:bg-red-50',
};

/** Button classes, for a link that should look like a button (sign-in goes by full-page navigation). */
export function buttonClass(kind: ButtonKind = 'primary', small = false): string {
  return `${BUTTON[kind]} ${small ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'} rounded-full font-medium transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`;
}

export function Button({ kind = 'primary', small, className = '', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { kind?: ButtonKind; small?: boolean }) {
  return <button type="button" {...props} className={`${buttonClass(kind, small)} ${className}`} />;
}

type Tone = 'blue' | 'green' | 'yellow' | 'red' | 'gray';

const BADGE: Record<Tone, string> = {
  blue: 'bg-totum-blue/10 text-totum-ink',
  green: 'bg-green-100 text-green-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  red: 'bg-red-100 text-red-800',
  gray: 'bg-gray-100 text-gray-700',
};

export function Badge({ tone = 'gray', icon, children }: { tone?: Tone; icon?: ReactNode; children: ReactNode }) {
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full font-medium inline-flex items-center gap-1 whitespace-nowrap ${BADGE[tone]}`}>
      {icon && <span aria-hidden="true">{icon}</span>}
      {children}
    </span>
  );
}

/**
 * A headline number with its change against the previous period. The change
 * is coloured by whether up is good for this metric (bounce rate: down is
 * good), and always carries an arrow and a sign, never colour alone.
 */
export function StatTile({
  label,
  value,
  current,
  previous,
  upIsGood = true,
  hint,
}: {
  label: string;
  value: string;
  current?: number;
  previous?: number;
  upIsGood?: boolean;
  hint?: string;
}) {
  const change = current !== undefined && previous !== undefined ? formatChange(current, previous) : null;
  const good = change && change.direction !== 'flat' ? (change.direction === 'up') === upIsGood : null;
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
      <p className="text-sm text-gray-600">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900 tabular">{value}</p>
      <p className="mt-2 text-xs text-gray-500 min-h-4">
        {change ? (
          <span className={good === null ? 'text-gray-600' : good ? 'text-green-700' : 'text-red-700'}>
            <span aria-hidden="true">{change.direction === 'up' ? '▲' : change.direction === 'down' ? '▼' : '■'}</span> {change.label}
            <span className="text-gray-500"> vs previous period</span>
          </span>
        ) : (
          hint ?? 'No earlier period to compare'
        )}
      </p>
    </div>
  );
}

export function Tabs<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: { value: T; label: string }[]; label: string }) {
  return (
    <div role="tablist" aria-label={label} className="inline-flex gap-1 rounded-2xl bg-gray-100 p-1 flex-wrap">
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          type="button"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={`px-3 py-1 text-sm rounded-full transition-colors ${value === o.value ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-600 hover:text-gray-900'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="text-center py-10 px-4">
      <p className="font-medium text-gray-900">{title}</p>
      {children && <div className="mt-1 text-sm text-gray-600 max-w-prose mx-auto">{children}</div>}
    </div>
  );
}

export function ErrorNote({ error, onRetry }: { error: { message: string }; onRetry?: () => void }) {
  return (
    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-center justify-between gap-4">
      <span>{error.message}</span>
      {onRetry && <Button kind="danger" small onClick={onRetry}>Try again</Button>}
    </div>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="py-10 text-center text-sm text-gray-500" role="status">
      <span className="inline-block w-4 h-4 mr-2 align-[-3px] rounded-full border-2 border-gray-300 border-t-totum-blue animate-spin" />
      {label}…
    </div>
  );
}

export function Field({ label, hint, children, htmlFor }: { label: string; hint?: ReactNode; children: ReactNode; htmlFor: string }) {
  return (
    <div className="mb-4">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

export const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-totum-blue focus:border-totum-blue';

/** Copy text, and say so. */
export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  return (
    <Button
      kind="secondary"
      small
      onClick={async (e) => {
        const el = e.currentTarget;
        await navigator.clipboard.writeText(text);
        el.textContent = 'Copied';
        setTimeout(() => (el.textContent = label), 1_500);
      }}
    >
      {label}
    </Button>
  );
}
